<script setup lang="ts">
/**
 * /carriers —— 承运商入驻(v2 · O4 按画板重排,深色;S-45)。
 *
 * 人在这里要能做完整条链路(与 S-01 那版一字未变的能力):
 *   ① 看承运商自助提交进来的申请(正常走小程序那条路;下面那个种子按钮见「已知悬案」)
 *   ② 把它从「待审」点到「已入驻」,填 8 → 页面显示 8%(库里存的是 800,但页面不摆内部单位,Y-59)
 *   ③ 运营代填再建一家(M-05 的第二种入驻模式:客户经理线下谈好后补录,走同一条链路)
 *   ④ 给这家公司录一张 3 人名单(姓名 / 证件号 / 手机号)
 *   ⑤ 把同一个手机号录到第二家公司,看到它被拒绝
 *
 * ═══ 与画板的差异(都记在 design-copy 的 O4 帧里)═══
 *
 * ① 队列画了六列,其中「联系人」「材料 n / n」两列**不渲染**:队列接口明文只给
 *    四个字段(申请编号 / 企业名 / 状态 / 提交时间),材料与联系人在单条详情里。
 *    一屏 100 条、每条都带上明文手机号与签名 URL,等于每刷一次列表就发 100 个
 *    能打开申请人材料的链接 —— M-27 拦下的正是这条 N+1。两列登记在帧的豁免里。
 * ② 审核动作是**画板没画的补件**:通过 / 拒绝 / 佣金率 / 审核意见都在行尾「审核」
 *    打开的抽屉里(行入口与 O1 的「详情」、O2 的单号入口同款)。画板没画不等于
 *    可以丢 —— 那是 S-01 留下的验收链路。
 * ③ 统计四张卡里三张没有数据源(见下面那三条 @unimplemented);「在册承运商」=
 *    listCarriers 的 total(服务端计数,真数)。
 * ④ 画板样例行里 approved 的词是「已通过」,而单点表里那个取值叫「已入驻」——
 *    行标签一律走 carrierApplicationStatusLabel(),画板那个词登记在帧的豁免里
 *    (改表词是一条跨端文案决策,不在本切片)。
 * ⑤ 代填入口(在册承运商卡右上角那颗按钮)是画板页脚点名要有、而画板自己没画的
 *    那半边;审核抽屉 / 员工名单那几张卡是换版前就在这个页面上的,一个都不丢。
 *
 * ═══ 已知悬案(不是本切片引入,本切片也没有动它)═══
 *
 * 「造一条待审申请」那颗按钮走的是 POST /api/carrier-applications,S-24 起那条路
 * 要求小程序令牌,运营令牌过不去 —— 点了是 401(界面上可见的失败,不是静默失效),
 * 处置(删按钮 / 换成运营代填 / 给它一条独立通道)在 M-05 的 2026-10-11 补注与
 * docs/miniapp-manual-walkthrough.md 里等人拍板。重排按「不替人拍板」保留原样。
 */
import { computed, h, onMounted, reactive, ref } from 'vue'
import {
  NButton,
  NInput,
  NModal,
  NSpace,
  NSwitch,
  NTag,
  useMessage,
  type DataTableColumns,
} from 'naive-ui'
import {
  DataTable,
  ErrorBanner,
  FieldGrid,
  NavIcon,
  PageHeader,
  PanelCard,
  StatTile,
  StatusTag,
  carrierApplicationStatusGroup,
  carrierApplicationStatusLabel,
  carrierMaterialTypeLabel,
  type ScopeNote,
} from '@sea-drone/ui'
import { formatRateBp, formatShanghaiDateTime, parsePercentToBp } from '@sea-drone/shared'
import { api, ApiError, type Carrier, type CarrierApplication, type CarrierApplicationDetail, type Employee } from '../api/client'

/** 这一页是运营端的深色页,底座由 S-41 的第三档 tone 给(AppShell 上已挂同一档) */
const TONE = 'ops-dark' as const

const message = useMessage()

const applications = ref<CarrierApplication[]>([])
const carriers = ref<Carrier[]>([])
const selectedCarrierId = ref<string | null>(null)
const employees = ref<Employee[]>([])
const lastError = ref<string | null>(null)
/** 返回体里的 total —— 服务端计数,不是本页行数 */
const applicationTotal = ref(0)
const carrierTotal = ref(0)

/**
 * 佣金率列内编辑的百分数草稿(carrierId → 输入框里的文本,Y-59)。
 *
 * 为什么必须有这份草稿:输入框如果是非受控的(只在挂载时取一次 defaultValue),
 * 运营把 8 改成 9、提交失败、再点刷新之后,输入框还停在 9 而旁边是 8% ——
 * 页面没有任何提示,运营会以为 9 已经生效。受控输入 + 刷新成功后清空草稿,
 * 才能保证「框里显示的就是库里的值」。
 */
const rateDrafts = reactive<Record<string, string>>({})

// 审核通过时填的佣金率。人填百分数,存进去是整数 bp(M-11 / M-14)
const percentInput = ref('8')
const previewBp = computed<number | null>(() => {
  try {
    return parsePercentToBp(percentInput.value)
  } catch {
    /* @intentional-swallow(reason="这是输入框的实时预览。填到一半的百分数(比如「8.」)本来就不合法,预览显示「—」即可;真正提交时会再算一次并把错误抛给用户") */
    return null
  }
})

// ── 运营代填(M-05 的第二种入驻模式):同一颗「新建承运商」的动作,发起人是运营 ──
//
// 画板页脚点名「代填入口在承运商那一块」,而画板自己没画它 —— 它是补件。
// 走的是契约里那条 POST /api/admin/carriers,与自助申请审核通过后建出来的公司
// 是同一种记录(代填只是把「谈好之后补录」这一步做进界面)。
const createOpen = ref(false)
const creating = ref(false)
const newName = ref('')
const newPercent = ref('8')
const newSelfOperated = ref(false)
const newBpPreview = computed<number | null>(() => {
  try {
    return parsePercentToBp(newPercent.value)
  } catch {
    /* @intentional-swallow(reason="代填表单的实时预览,与审核抽屉里那一处同一个口径:填到一半的百分数本来就不合法,预览显示「—」;提交时会再算一次并把错误抛给用户") */
    return null
  }
})

// 名单录入:每行「姓名,证件号,手机号」
const rosterText = ref(
  ['张伟,330903199001011234,13800138001', '李娜,330903199202022345,13800138002', '王强,330903199303033456,13800138003'].join(
    '\n',
  ),
)

const selectedCarrier = computed(() => carriers.value.find((c) => c.carrierId === selectedCarrierId.value) ?? null)

/**
 * 抽屉里正在审的那一条,**从列表里现取**而不是存一份快照 ——
 * 通过之后 refresh() 会把列表换掉,存快照的抽屉会一直显示旧状态,
 * 而表格里那一行已经是新的。同一个页面上两个状态互相矛盾,
 * 人只会相信其中一个,而且不会知道另一个是旧的。
 */
const reviewTargetId = ref<string | null>(null)
const reviewTarget = computed(
  () => applications.value.find((a) => a.applicationId === reviewTargetId.value) ?? null,
)

/**
 * 打开那一条的**完整材料**(M-27):信用代码 / 联系人 / 联系电话(明文)/
 * 营业执照与运输资质(短时效签名 URL)。列表里刻意没有它们。
 */
const detail = ref<CarrierApplicationDetail | null>(null)
const detailLoading = ref(false)
const detailError = ref<string | null>(null)

/** 拉一条申请的详情。**同一时刻只认当前抽屉里那一条** —— 拉的过程中人换了另一条,
 *  后到的旧响应会把新材料盖掉,而页面上看起来一切正常 */
async function loadDetail(applicationId: string): Promise<void> {
  detailLoading.value = true
  detailError.value = null
  try {
    const d = await api.getApplicationDetail(applicationId)
    if (reviewTargetId.value === applicationId) detail.value = d
  } catch (e) {
    if (reviewTargetId.value === applicationId) {
      // 「没读到」与「没有材料」必须分得开:读失败时详情置空、错误显示出来,
      // 绝不用一条空材料列表冒充「这条申请没有材料」
      detail.value = null
      detailError.value = e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e)
    }
  } finally {
    if (reviewTargetId.value === applicationId) detailLoading.value = false
  }
}

function openReview(applicationId: string): void {
  reviewTargetId.value = applicationId
  detail.value = null
  detailError.value = null
  void loadDetail(applicationId)
}

/** 链接过期用既有的判断(urlExpiresAt 早于此刻)—— 与监管端订单详情同一套口径 */
function linkExpired(att: { urlExpiresAt: string }): boolean {
  return new Date(att.urlExpiresAt).getTime() < Date.now()
}

const reviewOpen = computed({
  get: () => reviewTarget.value !== null,
  set: (open: boolean) => {
    if (!open) {
      reviewTargetId.value = null
      detail.value = null
      detailError.value = null
      detailLoading.value = false
    }
  },
})

// ── 三张统计卡的缺口(画「—」的三条理由各不相同)────────────────────
//
// @unimplemented(reason="「待审申请」要按状态过滤或一个计数接口,而契约 opsListCarrierApplications 只有 page/pageSize,返回体的 total 是**全部**申请数不是待审数(S-42 在 O1 上实测过同一处:拿申请总数顶替,页面上完全看不出来)。不许数本页条数。要么给队列加 status 参数,要么加一个计数接口 —— 两条都要先改契约", owner="me", blocks="O4 统计卡「待审申请」")
//
// @unimplemented(reason="「在册飞手」要的是全部承运商的员工名单总人数,而契约只有按承运商的 GET /api/admin/carriers/{carrierId}/employees(逐家拉是每页 N 次请求的代用口径),没有任何总计数端点。不许拿「本页某一家名单的长度」顶替 —— 那是另一个问题的答案", owner="me", blocks="O4 统计卡「在册飞手」")
//
// @unimplemented(reason="「本月新增」要按创建时间区间数申请或公司,而 CarrierApplication 与 Carrier 的返回体里都没有任何可用作「本月」的口径的计数接口(队列只有 page/pageSize;Settlement 那处同型缺口见 S-43)。拿「全部申请数」或「本页条数」顶替都会在页面上看不出来。要它先加时间区间参数或一个计数端点", owner="me", blocks="O4 统计卡「本月新增」")

const NOTES: readonly ScopeNote[] = [
  {
    title: '这一页能做什么',
    body:
      '入驻的两条路都在这:承运商在小程序里自助提交(进左边这张队列)→ 运营点行尾「审核」把「待审」' +
      '处理成通过(填一个佣金率,页面按百分数显示)或拒绝;客户经理线下谈好的一家,走' +
      '「在册承运商」卡右上角的「代填一家」补录 —— 两条路建出来的是同一种公司记录(M-05)。' +
      '点「查看 / 录名单」给某一家录一张名单(姓名 / 证件号 / 手机号),同一个手机号录到第二家会被拒绝。',
    refs: ['S-01', 'M-05', 'M-11', 'M-27'],
  },
  {
    title: '四张统计卡的数字从哪来',
    body:
      '「在册承运商」是服务端计数(carriers 列表的 total,不止本页)。另外三张契约里**没有数据源**:' +
      '入驻申请队列只有分页、没有状态过滤也没有计数接口(它的 total 是全部申请数,不是待审数);' +
      '员工名单只有按承运商的接口,没有总人数;申请与公司都没有任何能算「本月」的时间口径参数 —— ' +
      '所以画「—」,各登记一条 @unimplemented。**不许数本页条数**:把一页里的数说成全量,' +
      '页面上完全看不出来,演示时说出口就是一个错数字(同 Y-26 / AS-087 的同一口径)。',
    refs: ['S-45', 'M-27'],
  },
  {
    title: '队列里为什么没有「联系人」「材料」两列',
    body:
      '画板画了六列,而队列接口明文只给四个字段(申请编号 / 企业名 / 状态 / 提交时间):' +
      '一屏 100 条,每条都带上明文手机号与签名 URL,等于每刷一次列表就发 100 个能打开申请人材料的链接。' +
      '材料与联系人在**单条详情**里(M-27):点行尾「审核」打开抽屉,信用代码 / 联系人 / 联系电话(明文)/' +
      '营业执照与运输资质(短时效签名 URL)全在那里。逐行去拉详情凑这两列,正是 M-27 拦下的那条 N+1。',
    refs: ['M-27'],
  },
]

/** 挂在「在册承运商」那张卡上:它说的正是这一列为什么有两种写法 */
const CARRIER_NOTES: readonly ScopeNote[] = [
  {
    title: '佣金率为什么只显示百分数',
    body:
      '人填和看的都是百分数（最多 2 位小数）：库里存的是整数最小单位（M-14），' +
      '换算在界面边界完成（Y-59）。填 8 就是 8%；页面不再把内部的「800」摆出来 —— ' +
      '那只会让人怀疑自己填错了。',
    refs: ['M-11', 'M-14', 'Y-59'],
  },
]

/** 挂在③员工名单那张卡上 */
const ROSTER_NOTES: readonly ScopeNote[] = [
  {
    title: '员工名单是权限的唯一真相源',
    body: '整表替换，不是增量。提交之后名单外的手机号立刻失去飞手权限 —— 这就是权限的唯一真相源（M-05 / M-19）。',
    refs: ['M-05', 'M-19'],
  },
  {
    title: '拉黑与删除的区别',
    body:
      '每行第四项可选填「拉黑」两个字：这个人留在名单里，但下一次鉴权只会解析出客户角色（M-05）。' +
      '拉黑而不是删掉，是因为删了之后「从来没在册」与「被拉黑过」在库里长得一模一样 —— ' +
      '而整表替换时，拉黑标记要随这份名单一起重新提交，不带上它下一轮会把人静默洗白。',
    refs: ['M-05'],
  },
]

/** 挂在抽屉的「申请信息」卡上 */
const APPLICATION_NOTES: readonly ScopeNote[] = [
  {
    title: '列表四个字段,材料都在这一条里',
    body:
      '队列列表只有 企业名 / 状态 / 提交时间 / 审核意见 —— 一屏 100 条,把明文手机号和材料链接都带上,' +
      '等于每刷一次列表就发 100 个能打开申请人材料的链接。点开哪一条才拉哪一条的完整材料:' +
      '统一社会信用代码、联系人、联系电话、营业执照与运输资质。它们从申请提交那一刻就在库里,' +
      '以前只是没有读出来的路径,现在这里能看到全部(M-27)。',
    refs: ['M-27'],
  },
]

/** 挂在抽屉的「材料附件」卡上 */
const MATERIAL_NOTES: readonly ScopeNote[] = [
  {
    title: '材料走短时效签名 URL,过期后重新签发',
    body:
      '材料文件从来不给长期链接:每次点开这一条拿到的都是服务端现签的一版,1 小时后过期' +
      '(Y-11 的口径,与飞手传的凭证照片同一套)。过期不自动续,点「重新签发链接」重新拉一次即可。' +
      '库里存的是对象键,这里给的链接不是它 —— 键不进任何返回体。' +
      '每份材料带着自己的类型(营业执照 / 运输资质):类型由申请人提交时一并给出、原样落库,' +
      '不由服务端解析文件名的命名来猜。',
    refs: ['M-27', 'Y-02', 'Y-11'],
  },
  {
    title: '联系电话在这里是明文,这是有意的',
    body:
      '与员工名单的脱敏口径刻意不同:审核人要靠这个号码联系申请人核实资料,脱敏串打不出去。' +
      '它来自申请人登录时微信授权的那一次(服务端从令牌派生)—— 申请人自己改不了,' +
      '审核队列里也不会出现第二个来源的号码(M-27 / M-17)。',
    refs: ['M-27', 'M-17'],
  },
]

/** 挂在抽屉的「审核」卡上 */
const REVIEW_NOTES: readonly ScopeNote[] = [
  {
    title: '两条写死的备注',
    body:
      '通过时写进审计日志的备注是「运营端审核通过,费率 X%」,驳回时是「资料不全」。' +
      '它们是要留痕的固定说明,不做成让人随手改的输入框;费率按百分数写(Y-59)。',
    refs: ['S-01'],
  },
]

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

/**
 * 拉一次列表。失败时把错误弹出来并留在页面顶部的红条里,不往外抛 ——
 * 它的每一个调用方都只会原样再弹一遍。
 * 注意这里不是「吞掉」:错误既进了 message 也进了 lastError,人看得见。
 */
async function refresh(): Promise<void> {
  try {
    const [apps, cs] = await Promise.all([api.listApplications(), api.listCarriers()])
    applications.value = apps.items
    applicationTotal.value = apps.total
    carriers.value = cs.items
    carrierTotal.value = cs.total
    lastError.value = null
    // 刷新成功 = 库里的值已经拿到:清掉草稿,输入框回到库里的值。
    // 提交失败那条路不走这里,草稿保留(错误条会说明没有保存)。
    for (const carrierId of Object.keys(rateDrafts)) delete rateDrafts[carrierId]
    if (selectedCarrierId.value && !cs.items.some((c) => c.carrierId === selectedCarrierId.value)) {
      selectedCarrierId.value = null
      employees.value = []
    }
  } catch (e) {
    show(e)
  }
}

async function seedApplication(): Promise<void> {
  const stamp = new Date().toISOString().slice(11, 19).replace(/:/g, '')
  try {
    const created = await api.submitApplication({
      companyName: `舟山某某物流有限公司-${stamp}`,
      uniformCreditCode: `91330900MA${stamp}X`,
      contactName: '陈经理',
      contactPhone: '13900139000',
    })
    message.success(`已提交入驻申请:${created.companyName}`)
    await refresh()
  } catch (e) {
    show(e)
  }
}

async function approve(app: CarrierApplication): Promise<void> {
  let bp: number
  try {
    bp = parsePercentToBp(percentInput.value)
  } catch (e) {
    show(e)
    return
  }
  try {
    // 审计备注也写人话单位(Y-59):库里仍是 bp,但备注是给人读的
    await api.review(app.applicationId, {
      approve: true,
      commissionRateBp: bp,
      note: `运营端审核通过,费率 ${formatRateBp(bp)}`,
    })
    message.success(`${app.companyName} 已入驻,佣金率 ${formatRateBp(bp)}`)
    await refresh()
    // 审核之后详情要跟着变(状态、审核时间、已建公司)—— 抽屉还开着时它必须与库里一致
    if (reviewTargetId.value === app.applicationId) void loadDetail(app.applicationId)
  } catch (e) {
    show(e)
  }
}

async function reject(app: CarrierApplication): Promise<void> {
  try {
    await api.review(app.applicationId, { approve: false, note: '资料不全' })
    message.success(`${app.companyName} 已拒绝`)
    await refresh()
    if (reviewTargetId.value === app.applicationId) void loadDetail(app.applicationId)
  } catch (e) {
    show(e)
  }
}

/** 运营代填(M-05 的第二种模式):建一家与审核通过同型的承运商记录 */
async function createCarrier(): Promise<void> {
  const name = newName.value.trim()
  if (name === '') {
    message.warning('先填企业名称')
    return
  }
  let bp: number
  try {
    bp = parsePercentToBp(newPercent.value)
  } catch (e) {
    show(e)
    return
  }
  creating.value = true
  try {
    const created = await api.createCarrier({
      companyName: name,
      commissionRateBp: bp,
      selfOperated: newSelfOperated.value,
    })
    message.success(`已代填「${created.companyName}」,佣金率 ${formatRateBp(created.commissionRateBp)}`)
    createOpen.value = false
    newName.value = ''
    await refresh()
  } catch (e) {
    show(e)
  } finally {
    creating.value = false
  }
}

/** 整数 bp → 编辑框里的百分数文本(不带 % 号):800 → '8'、825 → '8.25' */
function percentText(bp: number): string {
  return formatRateBp(bp).replace(/%$/, '')
}

/**
 * 列表里的佣金率列内编辑(Y-59):人填百分数,提交前换成整数 bp。
 *
 * 三种失败各说各的,而且都不清草稿(运营填的东西不能凭空消失):
 *   · 空的 / 看不懂的百分数 → show(e)
 *   · 值与库里相同 → 直接清掉草稿,不发请求
 *   · 提交失败 → 顶部红条明确说「没有保存」,输入框保留草稿;
 *     运营可以改完再失焦重试,或点刷新让输入框回到库里的值
 */
async function commitPercent(row: Carrier, text: string): Promise<void> {
  const trimmed = String(text).trim()
  if (trimmed === '') {
    message.warning('佣金率不能为空 —— 填一个 0 到 100 的百分数')
    return
  }
  let bp: number
  try {
    bp = parsePercentToBp(trimmed)
  } catch (e) {
    show(e)
    return
  }
  if (bp === row.commissionRateBp) {
    delete rateDrafts[row.carrierId]
    return
  }
  try {
    await api.patchCarrier(row.carrierId, { commissionRateBp: bp })
    message.success(`${row.companyName}:佣金率已保存为 ${formatRateBp(bp)}`)
    await refresh()
  } catch (e) {
    const detail =
      e instanceof ApiError
        ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
        : e instanceof Error
          ? e.message
          : String(e)
    const msg = `${detail} —— 佣金率没有保存;输入框里仍是你填的值,可以改完再失焦重试,或点刷新回到库里的值`
    lastError.value = msg
    message.error(msg, { duration: 8000 })
  }
}

async function patch(c: Carrier, p: { commissionRateBp?: number; selfOperated?: boolean; enabled?: boolean }): Promise<void> {
  try {
    const after = await api.patchCarrier(c.carrierId, p)
    message.success(`${after.companyName}:佣金率 ${formatRateBp(after.commissionRateBp)} · 自营 ${after.selfOperated} · 启用 ${after.enabled}`)
    await refresh()
  } catch (e) {
    show(e)
  }
}

async function selectCarrier(c: Carrier): Promise<void> {
  selectedCarrierId.value = c.carrierId
  try {
    employees.value = (await api.listEmployees(c.carrierId)).items
  } catch (e) {
    employees.value = []
    show(e)
  }
}

/**
 * 把 textarea 解析成名单。**格式不对的行直接抛错,不跳过。**
 * 静默跳过一行的后果是:运营以为 3 个人都录进去了,实际只进了 2 个,
 * 而没进去的那个人登录小程序时只看到客户端 —— 到那时没人会想到是这里少了一行。
 *
 * 第 4 项(可选)是拉黑标记(M-05):填「拉黑」两个字即 blacklisted=true。
 * 不认第四项拼写之外的值 —— 「拉黑」写成拼音或别的词时整行抛错,
 * 而不是被猜成 false(猜错的后果是一个被拉黑的人静默恢复飞手权限)。
 */
function parseRoster(text: string): { name: string; idNumber: string; phone: string; blacklisted?: boolean }[] {
  const out: { name: string; idNumber: string; phone: string; blacklisted?: boolean }[] = []
  const lines = text.split(/\r?\n/)
  for (let i = 0; i < lines.length; i++) {
    const line = (lines[i] ?? '').trim()
    if (line === '') continue
    const parts = line.split(/[,，\t]/).map((s) => s.trim())
    const [name, idNumber, phone, blacklistedFlag] = parts
    if (parts.length < 3 || !name || !idNumber || !phone) {
      throw new Error(`第 ${i + 1} 行格式不对(要「姓名,证件号,手机号」三项,第四项可选填「拉黑」):${line}`)
    }
    if (parts.length > 4 || (blacklistedFlag !== undefined && blacklistedFlag !== '' && blacklistedFlag !== '拉黑')) {
      throw new Error(
        `第 ${i + 1} 行格式不对(第四项只认「拉黑」两个字,不认的写法不许猜):${line}`,
      )
    }
    out.push({ name, idNumber, phone, ...(blacklistedFlag === '拉黑' ? { blacklisted: true } : {}) })
  }
  return out
}

async function replaceRoster(): Promise<void> {
  const carrierId = selectedCarrierId.value
  if (!carrierId) {
    message.warning('先在上面的承运商列表里选一家')
    return
  }
  let rows: { name: string; idNumber: string; phone: string }[]
  try {
    rows = parseRoster(rosterText.value)
  } catch (e) {
    show(e)
    return
  }
  try {
    const page = await api.replaceEmployees(carrierId, rows)
    employees.value = page.items
    message.success(`名单已整表替换为 ${page.total} 人;名单外的手机号即刻失去飞手权限`)
  } catch (e) {
    show(e)
  }
}

/**
 * 抽屉里的字段,全部取自**详情**(M-27)。
 *
 * 还在读 / 读失败时不显示任何替代值:读失败会以错误条出现并有重试,
 * 「没读到」与「这条申请就这么几项」必须分得开。
 */
const applicationFields = computed(() => {
  const d = detail.value
  if (d === null) return []
  return [
    { label: '企业名', value: d.companyName },
    // 状态词走单点表(与队列表格、与另外两个端同一个来源)
    { label: '状态', value: carrierApplicationStatusLabel(d.status) },
    { label: '统一社会信用代码', value: d.uniformCreditCode },
    { label: '联系人', value: d.contactName },
    // 明文,刻意不脱敏(M-27):审核人要打这个电话联系申请人 —— 理由挂在材料卡的注释里
    { label: '联系电话(审核人联系用,明文)', value: d.contactPhone },
    { label: '提交时间', value: formatShanghaiDateTime(d.submittedAt) },
    { label: '审核时间', value: d.reviewedAt === null ? '—' : formatShanghaiDateTime(d.reviewedAt) },
    { label: '审核意见', value: d.reviewNote ?? '—', span: 2 as const },
    { label: '申请编号', value: d.applicationId, mono: true, span: 2 as const },
    { label: '建成公司编号', value: d.carrierId ?? '—', mono: true, span: 2 as const },
  ]
})

/**
 * 队列四列 + 操作列,逐列一的数据源(画板 O4 的表头逐字):
 *   申请编号 applicationId(等宽)· 企业名称 companyName · 提交时间 submittedAt(东八区)
 *   · 状态 status(StatusTag,中文与配色走单点表)
 * 画板另外画的「联系人」「材料 n / n」两列**不在这份列定义里**:队列接口不给它们,
 * 逐行拉详情正是 M-27 拦下的 N+1(理由挂在 NOTES 与 design-copy 的 O4 帧里)。
 * 「审核」是补件(画板没画行入口):通过 / 拒绝 / 佣金率 / 审核意见都在抽屉里。
 */
const applicationColumns = computed<DataTableColumns<CarrierApplication>>(() => [
  {
    title: '申请编号',
    key: 'applicationId',
    width: 200,
    render: (row) => h('span', { class: 'app-no' }, row.applicationId),
  },
  { title: '企业名称', key: 'companyName', width: 260 },
  { title: '提交时间', key: 'submittedAt', width: 180, render: (row) => formatShanghaiDateTime(row.submittedAt) },
  {
    title: '状态',
    key: 'status',
    width: 110,
    // 用订单状态那个 pill(StatusTag),只是文案与分组换一套 —— 两个端的状态标签
    // 必须长得一模一样,各写一份 n-tag 的话它们迟早会不一样,而两张表不在同一屏里
    render: (row) =>
      h(StatusTag, {
        status: row.status,
        label: carrierApplicationStatusLabel(row.status),
        group: carrierApplicationStatusGroup(row.status),
        tone: TONE,
        size: 'sm',
      }),
  },
  {
    title: '操作',
    key: 'actions',
    width: 100,
    // 已审的申请也要能点开:材料在详情里,「审过之后材料就看不到了」正是这条链路当初悬空的形状(M-27)
    render: (row) =>
      h(
        NButton,
        { size: 'tiny', type: row.status === 'pending' ? 'primary' : 'default', onClick: () => openReview(row.applicationId) },
        { default: () => (row.status === 'pending' ? '审核' : '查看') },
      ),
  },
])

const carrierColumns = computed<DataTableColumns<Carrier>>(() => [
  {
    title: '公司',
    key: 'companyName',
    width: 260,
    render: (row) =>
      h('span', { class: 'carrier-name' }, [
        row.companyName,
        row.carrierId === selectedCarrierId.value
          ? h(NTag, { type: 'info', size: 'small', class: 'current-tag' }, { default: () => '当前' })
          : null,
      ]),
  },
  {
    title: '佣金率',
    key: 'commissionRateBp',
    width: 260,
    // 只显示百分数(Y-59):内部 bp 不出现在这一列里。
    // 受控输入:value 取自草稿(没有草稿时取库里的值),刷新成功后草稿清空。
    render: (row) =>
      h('span', { class: 'rate-cell' }, [
        h('strong', formatRateBp(row.commissionRateBp)),
        h(
          NInput,
          {
            value: rateDrafts[row.carrierId] ?? percentText(row.commissionRateBp),
            size: 'tiny',
            class: 'rate-input',
            'onUpdate:value': (v: string) => {
              rateDrafts[row.carrierId] = v
            },
            // 失焦时提交:逐字符提交会把「8.」这种填到一半的状态报成错
            onBlur: (e: FocusEvent) => {
              const text = (e.target as HTMLInputElement | null)?.value
              if (typeof text === 'string') rateDrafts[row.carrierId] = text
              void commitPercent(row, rateDrafts[row.carrierId] ?? percentText(row.commissionRateBp))
            },
          },
          { suffix: () => '%' },
        ),
      ]),
  },
  {
    title: '自营（优先窗口只对自营开）',
    key: 'selfOperated',
    width: 200,
    render: (row) =>
      h(NSwitch, {
        value: row.selfOperated,
        'onUpdate:value': (v: boolean) => void patch(row, { selfOperated: v }),
      }),
  },
  {
    title: '启用',
    key: 'enabled',
    width: 100,
    render: (row) =>
      h(NSwitch, {
        value: row.enabled,
        'onUpdate:value': (v: boolean) => void patch(row, { enabled: v }),
      }),
  },
  {
    title: '员工名单',
    key: 'employees',
    width: 130,
    render: (row) =>
      h(NButton, { size: 'tiny', onClick: () => void selectCarrier(row) }, { default: () => '查看 / 录名单' }),
  },
])

const employeeColumns = computed<DataTableColumns<Employee>>(() => [
  { title: '姓名', key: 'name', width: 160 },
  { title: '手机号（脱敏）', key: 'phoneMasked', width: 200 },
  { title: '证件号（脱敏）', key: 'idNumberMasked' },
  {
    // 拉黑标记(M-05)。读不出来时运营只能靠记 —— 谁被拉黑过、什么时候
    title: '状态',
    key: 'blacklisted',
    width: 100,
    render: (row) =>
      row.blacklisted
        ? h('span', { class: 'blacklist-tag' }, '已拉黑')
        : h('span', { class: 'hint-text' }, '在册'),
  },
])

onMounted(() => {
  void refresh()
})
</script>

<template>
  <div class="page">
    <PageHeader :tone="TONE" title="承运商" :notes="NOTES">
      <template #actions>
        <!-- 画板页头右端那一行:先说明(逐字)、再刷新(幽灵键,照 O2 / O3 那一颗) -->
        <span class="header-note">入驻两条路:自助申请、运营代填</span>
        <button type="button" class="ghost" @click="refresh">
          <NavIcon name="refresh" :size="14" />
          刷新
        </button>
      </template>
    </PageHeader>

    <ErrorBanner v-if="lastError" :tone="TONE" :message="lastError" title="上一次操作失败" />

    <!--
      四张统计卡(画板逐字)。「在册承运商」= 服务端计数;另外三张契约里没有数据源,
      画「—」+ 各一条 @unimplemented 标记(理由见源码里那三条)。色条按画板:
      待审申请 = 琥珀、在册承运商 / 在册飞手 = 品牌蓝、本月新增 = 绿
    -->
    <div class="stats">
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="待审申请" value="—" tone="warning" />
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="在册承运商" :value="carrierTotal" tone="brand" />
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="在册飞手" value="—" tone="brand" />
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="本月新增" value="—" tone="success" />
    </div>

    <!-- 入驻申请队列:画板那四列 + 行尾「审核」(补件);种子按钮保持原样(见文件头「已知悬案」) -->
    <PanelCard :tone="TONE" padding="none" :subtitle="`共 ${applicationTotal} 条申请（全部，服务端计数）`">
      <template #extra>
        <n-button size="small" @click="seedApplication">造一条待审申请（正常由承运商自助提交）</n-button>
      </template>
      <DataTable
        :tone="TONE"
        :columns="applicationColumns"
        :data="applications"
        :row-key="(row: CarrierApplication) => row.applicationId"
        empty-text="队列里没有申请"
      />
    </PanelCard>

    <!-- 在册承运商 + 代填入口(M-05 的第二种入驻模式;画板页脚点名要有、画板自己没画) -->
    <PanelCard :tone="TONE" title="在册承运商" :notes="CARRIER_NOTES" padding="none">
      <template #extra>
        <span class="hint-text">在册 {{ carrierTotal }} 家</span>
        <n-button size="small" @click="createOpen = true">代填一家</n-button>
      </template>
      <DataTable
        :tone="TONE"
        :columns="carrierColumns"
        :data="carriers"
        :row-key="(row: Carrier) => row.carrierId"
        empty-text="还没有在册承运商 —— 先通过一条申请，或点右上角「代填一家」补录"
      />
    </PanelCard>

    <PanelCard
      :tone="TONE"
      :title="`员工名单${selectedCarrier ? '：' + selectedCarrier.companyName : '（先选一家承运商）'}`"
      :notes="ROSTER_NOTES"
    >
      <n-space vertical>
        <n-input
          v-model:value="rosterText"
          type="textarea"
          :rows="5"
          class="roster-input"
          placeholder="每行一个人：姓名,证件号,手机号；第四项可选填「拉黑」"
        />
        <n-space align="center">
          <n-button type="primary" :disabled="!selectedCarrierId" @click="replaceRoster">整表替换名单</n-button>
          <span class="hint-text">同一个手机号录到第二家公司会被拒绝，报错里会说是哪家占着</span>
        </n-space>
      </n-space>
      <DataTable
        class="roster-table"
        :tone="TONE"
        :columns="employeeColumns"
        :data="employees"
        :row-key="(row: Employee) => row.idNumberMasked"
        empty-text="名单为空"
      />
    </PanelCard>

    <!-- 页脚说明(画板那句的落地版:画板把两条路画成「标签页」,这里是一页里叠着的三块) -->
    <p class="foot-note">
      代填入口在「在册承运商」那一块,用于客户经理线下谈好后补录 —— 走的是同一条入驻链路,只是发起人是运营。
    </p>

    <n-drawer v-model:show="reviewOpen" :width="640" placement="right">
      <n-drawer-content v-if="reviewTarget" :native-scrollbar="false">
        <template #header>
          <div class="drawer-head">
            <span class="drawer-title">{{ reviewTarget.companyName }}</span>
            <StatusTag
              :status="reviewTarget.status"
              :label="carrierApplicationStatusLabel(reviewTarget.status)"
              :group="carrierApplicationStatusGroup(reviewTarget.status)"
              :tone="TONE"
            />
          </div>
        </template>

        <div class="drawer-body">
          <PanelCard :tone="TONE" title="申请信息" :notes="APPLICATION_NOTES">
            <div v-if="detailLoading" class="hint-text">正在读取这条申请的完整材料…</div>
            <template v-else-if="detailError !== null">
              <ErrorBanner :tone="TONE" :message="detailError" title="读取申请详情失败" />
              <n-space align="center" class="detail-retry">
                <n-button size="tiny" @click="reviewTarget && loadDetail(reviewTarget.applicationId)">重试</n-button>
                <span class="hint-text">读不出来时不显示任何替代值 ——「没读到」与「这条申请没有材料」不是一回事</span>
              </n-space>
            </template>
            <FieldGrid v-else-if="detail !== null" :tone="TONE" :items="applicationFields" />
          </PanelCard>

          <PanelCard v-if="detail !== null" :tone="TONE" title="材料附件" :notes="MATERIAL_NOTES">
            <div v-if="detail.attachments.length === 0" class="hint-text">
              这条申请没有上传材料 —— 申请时附件是可选的,这是「申请人没传」,不是读取失败。
            </div>
            <div v-else class="material-list">
              <div v-for="(att, i) in detail.attachments" :key="i" class="material-row">
                <!-- 材料类型(材料带类型):审核人靠这三个字知道哪张是营业执照。
                     中文名只在 packages/ui 的标签表里定义一次 —— 这里手写一份的代价
                     是下一个人改词时改不到这一处,而两处各自读起来都正常 -->
                <span class="material-type">{{ carrierMaterialTypeLabel(att.type) }}</span>
                <template v-if="linkExpired(att)">
                  <span class="material-expired">链接已过期</span>
                </template>
                <template v-else>
                  <a :href="att.url" target="_blank" rel="noreferrer" class="material-link">打开材料</a>
                </template>
                <span class="hint-text">本链接到期 {{ formatShanghaiDateTime(att.urlExpiresAt) }}</span>
              </div>
            </div>
            <n-space align="center" class="material-refresh">
              <n-button size="tiny" :loading="detailLoading" @click="reviewTarget && loadDetail(reviewTarget.applicationId)">
                重新签发链接
              </n-button>
              <span class="hint-text">签名 URL 1 小时后过期,过期后重新签发一版即可;长期链接从不产出</span>
            </n-space>
          </PanelCard>

          <PanelCard v-if="reviewTarget.status === 'pending'" :tone="TONE" title="审核" :notes="REVIEW_NOTES">
            <n-space align="center" class="rate-row">
              <span class="field-label">审核通过时用的佣金率：</span>
              <n-input v-model:value="percentInput" class="percent-input" placeholder="8" />
              <n-tag :type="previewBp === null ? 'error' : 'success'">
                = {{ previewBp === null ? '—（这个百分数看不懂）' : formatRateBp(previewBp) }}
              </n-tag>
            </n-space>
            <n-space>
              <n-button size="small" type="primary" @click="approve(reviewTarget)">通过</n-button>
              <n-button size="small" @click="reject(reviewTarget)">拒绝</n-button>
            </n-space>
          </PanelCard>
        </div>
      </n-drawer-content>
    </n-drawer>

    <!-- 运营代填(M-05 的第二种入驻模式):与自助申请走同一条链路的补录入口 -->
    <n-modal v-model:show="createOpen" preset="card" title="运营代填一家承运商">
      <n-space vertical>
        <div class="field-label">企业名称</div>
        <n-input v-model:value="newName" placeholder="线下谈好的公司全称" />
        <div class="field-label">佣金率（填百分数，最多 2 位小数）</div>
        <n-space align="center">
          <n-input v-model:value="newPercent" class="percent-input" placeholder="8" />
          <n-tag :type="newBpPreview === null ? 'error' : 'success'">
            = {{ newBpPreview === null ? '—（这个百分数看不懂）' : formatRateBp(newBpPreview) }}
          </n-tag>
        </n-space>
        <n-space align="center">
          <n-switch v-model:value="newSelfOperated" />
          <span class="field-label">自营（优先窗口只对自营开）</span>
        </n-space>
      </n-space>
      <p class="modal-note">
        代填走的是同一条入驻链路（M-05）：客户经理线下谈好之后由运营补录，建出来的是与审核通过
        同型的公司记录；员工名单在这一家的「查看 / 录名单」里录。
      </p>
      <n-space justify="end" class="modal-actions">
        <n-button @click="createOpen = false">取消</n-button>
        <n-button type="primary" :loading="creating" @click="createCarrier">代填</n-button>
      </n-space>
    </n-modal>
  </div>
</template>

<style scoped>
/* 页头与各块之间的纵向节奏:画板是 16 —— 这里由 .page 的 gap 一处给 */
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 页头右端那句说明(画板:12px 三级灰,排在刷新键左边) */
.header-note {
  font-size: 12px;
  color: var(--sd-text3);
}

/* 刷新:画板的幽灵键(无底、1px 描边 border-strong、内边距 [8,14]、圆角 sm) */
.ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--sd-border-strong);
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.ghost:hover {
  color: var(--sd-text1);
  border-color: var(--sd-brand);
}

.stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.hint-text {
  font-size: 12px;
  color: var(--sd-text3);
}

/* 页脚说明(画板那一句的落地版:整宽一行,12px 三级灰) */
.foot-note {
  margin: 0;
  font-size: 12px;
  color: var(--sd-text3);
}

.field-label {
  font-size: 13px;
  color: var(--sd-text2);
}

.rate-row {
  margin-bottom: 12px;
}

.percent-input {
  width: 90px;
}

.modal-note {
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.7;
  color: var(--sd-text3);
}

.modal-actions {
  margin-top: 20px;
}

.roster-input :deep(textarea) {
  font-family: var(--sd-font-mono);
}

.roster-table {
  margin-top: 12px;
}

.drawer-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.drawer-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--sd-text1);
}

.drawer-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-retry {
  margin-top: 12px;
}

.material-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.material-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.material-type {
  min-width: 64px;
  font-size: 13px;
  color: var(--sd-text1);
}

.material-link {
  font-size: 13px;
  color: var(--sd-brand);
}

.material-expired {
  font-size: 13px;
  color: var(--sd-danger);
}

.material-refresh {
  margin-top: 12px;
}

/* 表格单元格由 DataTable 渲染,拿不到本组件的 scope 属性 —— 用 :deep 穿进去 */
:deep(.app-no) {
  font-family: var(--sd-font-mono);
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text2);
}

:deep(.blacklist-tag) {
  color: var(--sd-danger);
  font-size: 13px;
}

:deep(.rate-cell) {
  display: flex;
  align-items: center;
  gap: 8px;
}

:deep(.rate-eq) {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

:deep(.rate-input) {
  width: 120px;
}

:deep(.carrier-name) {
  display: flex;
  align-items: center;
  gap: 8px;
}

:deep(.current-tag) {
  margin-right: auto;
}

@media (max-width: 1100px) {
  .stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
